import React, { useState, useEffect } from 'react';
import { deletePrediction, insertPrediction, updatePrediction, watchPredictions } from '../../Config/matudb';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  CheckCircle, 
  XCircle, 
  Clock,
  Target,
  TrendingUp,
  Calendar,
  Filter,
  Search,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const PronosticosManager = () => {
  const [pronosticos, setPronosticos] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPronostico, setEditingPronostico] = useState(null);
  const [filter, setFilter] = useState('hoy');
  const [loading, setLoading] = useState(true);
  const [teamsDatabase, setTeamsDatabase] = useState([]);
  const [showHomeSuggestions, setShowHomeSuggestions] = useState(false);
  const [showAwaySuggestions, setShowAwaySuggestions] = useState(false);
  const [filteredHomeTeams, setFilteredHomeTeams] = useState([]);
  const [filteredAwayTeams, setFilteredAwayTeams] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('list'); // 'list' o 'kanban'
  const [draggedPronostico, setDraggedPronostico] = useState(null);
  const itemsPerPage = 5;

  // Formulario
  const [formData, setFormData] = useState({
    homeTeamName: '',
    homeTeamLogo: '',
    awayTeamName: '',
    awayTeamLogo: '',
    confidence: '',
    odds: '',
    prediction: '',
    date: '',
    time: ''
  });

  useEffect(() => {
    const stop = loadPronosticos();
    loadTeamsDatabase();
    return () => {
      if (typeof stop === 'function') stop();
    };
  }, []);

  const loadTeamsDatabase = () => {
    const collect = (rows, teams) => {
      rows.forEach((pronostico) => {
        if (pronostico.homeTeam?.name && pronostico.homeTeam?.logo) {
          teams.push({
            name: pronostico.homeTeam.name,
            logo: pronostico.homeTeam.logo,
            type: 'home'
          });
        }
        if (pronostico.awayTeam?.name && pronostico.awayTeam?.logo) {
          teams.push({
            name: pronostico.awayTeam.name,
            logo: pronostico.awayTeam.logo,
            type: 'away'
          });
        }
      });
    };

    const stopFree = watchPredictions('abet', (freeRows) => {
      const teams = [];
      collect(freeRows, teams);
      const stopVip = watchPredictions('abetvip', (vipRows) => {
        collect(vipRows, teams);
        const uniqueTeams = teams.filter((team, index, self) =>
          index === self.findIndex((t) => t.name.toLowerCase() === team.name.toLowerCase())
        );
        setTeamsDatabase(uniqueTeams.sort((a, b) => a.name.localeCompare(b.name)));
        stopVip();
      });
      stopFree();
    });
  };

  const loadPronosticos = () => {
    return watchPredictions('abet', (rows) => {
      setPronosticos(rows);
      setLoading(false);
    });
  };

  const handleTeamSearch = (searchTerm, teamType) => {
    if (!searchTerm.trim()) {
      if (teamType === 'home') {
        setFilteredHomeTeams([]);
        setShowHomeSuggestions(false);
      } else {
        setFilteredAwayTeams([]);
        setShowAwaySuggestions(false);
      }
      return;
    }

    const filtered = teamsDatabase.filter(team =>
      team.name.toLowerCase().includes(searchTerm.toLowerCase())
    ).slice(0, 5); // Limitar a 5 sugerencias

    if (teamType === 'home') {
      setFilteredHomeTeams(filtered);
      setShowHomeSuggestions(true);
    } else {
      setFilteredAwayTeams(filtered);
      setShowAwaySuggestions(true);
    }
  };

  const handleSearchSubmit = (e, teamType) => {
    e.preventDefault();
    const searchValue = teamType === 'home' ? formData.homeTeamName : formData.awayTeamName;
    setSearchTerm(searchValue);
    handleTeamSearch(searchValue, teamType);
  };

  const selectTeam = (team, teamType) => {
    if (teamType === 'home') {
      setFormData({
        ...formData,
        homeTeamName: team.name,
        homeTeamLogo: team.logo
      });
      setShowHomeSuggestions(false);
    } else {
      setFormData({
        ...formData,
        awayTeamName: team.name,
        awayTeamLogo: team.logo
      });
      setShowAwaySuggestions(false);
    }
  };

  const handleTeamInputChange = (value, teamType) => {
    if (teamType === 'home') {
      setFormData({
        ...formData,
        homeTeamName: value,
        homeTeamLogo: '' // Limpiar logo cuando se escribe manualmente
      });
      setShowHomeSuggestions(false); // Ocultar sugerencias al escribir
    } else {
      setFormData({
        ...formData,
        awayTeamName: value,
        awayTeamLogo: '' // Limpiar logo cuando se escribe manualmente
      });
      setShowAwaySuggestions(false); // Ocultar sugerencias al escribir
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const dateTime = new Date(`${formData.date}T${formData.time}`);
    const formattedDate = dateTime.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    const formattedTime = dateTime.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });

    const pronosticoData = {
      awayTeam: {
        logo: formData.awayTeamLogo,
        name: formData.awayTeamName
      },
      confidence: parseInt(formData.confidence),
      date: formattedDate,
      homeTeam: {
        logo: formData.homeTeamLogo,
        name: formData.homeTeamName
      },
      hours: formattedTime,
      odds: parseFloat(formData.odds),
      prediction: formData.prediction,
      state: "pendiente"
    };

    try {
      if (editingPronostico) {
        await updatePrediction('abet', editingPronostico.id, pronosticoData);
      } else {
        await insertPrediction('abet', pronosticoData);
      }

      resetForm();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error al guardar pronóstico:', error);
      alert('Error al guardar el pronóstico');
    }
  };

  const handleEdit = (pronostico) => {
    setEditingPronostico(pronostico);
    setFormData({
      homeTeamName: pronostico.homeTeam?.name || '',
      homeTeamLogo: pronostico.homeTeam?.logo || '',
      awayTeamName: pronostico.awayTeam?.name || '',
      awayTeamLogo: pronostico.awayTeam?.logo || '',
      confidence: (pronostico.confidence || '').toString(),
      odds: (pronostico.odds || '').toString(),
      prediction: pronostico.prediction || '',
      date: new Date().toISOString().split('T')[0], // Formato para input date
      time: '12:00'
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este pronóstico?')) {
      await deletePrediction('abet', id);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    await updatePrediction('abet', id, { state: newStatus });
  };

  const resetForm = () => {
    setFormData({
      homeTeamName: '',
      homeTeamLogo: '',
      awayTeamName: '',
      awayTeamLogo: '',
      confidence: '',
      odds: '',
      prediction: '',
      date: '',
      time: ''
    });
    setEditingPronostico(null);
    setShowHomeSuggestions(false);
    setShowAwaySuggestions(false);
  };

  const filteredPronosticos = pronosticos.filter(pronostico => {
    const today = new Date().toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    
    if (filter === 'hoy') {
      return pronostico.date === today;
    } else if (filter === 'todos') {
      return true;
    } else {
      return pronostico.state === filter;
    }
  });

  // Paginación
  const totalPages = Math.ceil(filteredPronosticos.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPronosticos = filteredPronosticos.slice(startIndex, endIndex);

  // Agrupar pronósticos del día por estado para Kanban
  const todayPronosticos = pronosticos.filter(pronostico => {
    const today = new Date().toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    return pronostico.date === today;
  });

  const kanbanColumns = {
    pendiente: todayPronosticos.filter(p => p.state === 'pendiente' || !p.state),
    ganado: todayPronosticos.filter(p => p.state === 'ganado'),
    perdido: todayPronosticos.filter(p => p.state === 'perdido'),
    nulo: todayPronosticos.filter(p => p.state === 'nulo')
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pendiente': return 'text-yellow-400';
      case 'ganado': return 'text-green-400';
      case 'perdido': return 'text-red-400';
      case 'nulo': return 'text-gray-400';
      default: return 'text-yellow-400';
    }
  };

  const getStatusBgColor = (status) => {
    switch (status) {
      case 'pendiente': return 'bg-yellow-900/20 border-yellow-700';
      case 'ganado': return 'bg-green-900/20 border-green-700';
      case 'perdido': return 'bg-red-900/20 border-red-700';
      case 'nulo': return 'bg-gray-900/20 border-gray-700';
      default: return 'bg-yellow-900/20 border-yellow-700';
    }
  };

  const handleDragStart = (e, pronostico) => {
    setDraggedPronostico(pronostico);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    if (draggedPronostico) {
      await handleStatusChange(draggedPronostico.id, newStatus);
      setDraggedPronostico(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-white">Gestión de Pronósticos</h2>
          <p className="text-white/60">Administra y publica pronósticos deportivos</p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
        >
          <Plus size={20} />
          <span>Nuevo Pronóstico</span>
        </button>
      </div>

      {/* Filtros y controles */}
      <div className="bg-white/10 backdrop-blur-lg rounded-lg p-4 border border-white/20">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div className="flex flex-wrap gap-2">
            {['hoy', 'todos', 'pendiente', 'ganado', 'perdido'].map((status) => (
              <button
                key={status}
                onClick={() => {
                  setFilter(status);
                  setCurrentPage(1);
                }}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  filter === status
                    ? 'bg-blue-600 text-white'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                {status === 'hoy' ? 'Hoy' : status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
          
          {filter === 'hoy' && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                  viewMode === 'list'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                Lista
              </button>
              <button
                onClick={() => setViewMode('kanban')}
                className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                  viewMode === 'kanban'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                Kanban
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Lista de Pronósticos */}
      {filter === 'hoy' && viewMode === 'kanban' ? (
        // Vista Kanban para pronósticos del día
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {Object.entries(kanbanColumns).map(([status, pronosticos]) => (
            <div
              key={status}
              className="bg-white/10 backdrop-blur-lg rounded-lg p-4 border border-white/20"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, status)}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${getStatusColor(status)}`}>
                  {status === 'pendiente' ? 'Pendientes' :
                   status === 'ganado' ? 'Ganados' :
                   status === 'perdido' ? 'Perdidos' : 'Nulos'}
                </h3>
                <span className="bg-white/10 text-white px-2 py-1 rounded-full text-sm">
                  {pronosticos.length}
                </span>
              </div>
              
              <div className="space-y-3">
                {pronosticos.map((pronostico) => (
                  <div
                    key={pronostico.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, pronostico)}
                    className={`p-3 rounded-lg border cursor-move transition-all duration-200 hover:scale-105 ${getStatusBgColor(pronostico.state || 'pendiente')}`}
                  >
                    <div className="flex items-center space-x-2 mb-2">
                      {pronostico.homeTeam?.logo && (
                        <img 
                          src={pronostico.homeTeam.logo} 
                          alt={pronostico.homeTeam.name}
                          className="w-4 h-4 rounded-full"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      )}
                      <span className="text-xs text-white font-medium">
                        {pronostico.homeTeam?.name}
                      </span>
                      <span className="text-xs text-white/60">vs</span>
                      {pronostico.awayTeam?.logo && (
                        <img 
                          src={pronostico.awayTeam.logo} 
                          alt={pronostico.awayTeam.name}
                          className="w-4 h-4 rounded-full"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      )}
                      <span className="text-xs text-white font-medium">
                        {pronostico.awayTeam?.name}
                      </span>
                    </div>
                    
                    <div className="text-xs text-white/80 mb-2">
                      {pronostico.prediction}
                    </div>
                    
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-white/60">
                        {pronostico.confidence}% @ {pronostico.odds}
                      </span>
                      <span className="text-white/60">
                        {pronostico.hours}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Vista de lista normal
        <div className="space-y-4">
          {paginatedPronosticos.length === 0 ? (
            <div className="bg-white/10 backdrop-blur-lg rounded-lg p-8 border border-white/20 text-center">
              <Target className="text-white/40 mx-auto mb-4" size={48} />
              <h3 className="text-lg font-semibold text-white mb-2">No hay pronósticos</h3>
              <p className="text-white/60">Crea tu primer pronóstico para comenzar</p>
            </div>
          ) : (
            <>
              {paginatedPronosticos.map((pronostico) => (
            <div
              key={pronostico.id}
              className="bg-white/10 backdrop-blur-lg rounded-lg p-4 border border-white/20"
            >
              <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-4 lg:space-y-0">
                <div className="flex-1">
                  <div className="flex items-center space-x-4 mb-2">
                    <span className={`text-sm font-medium ${getStatusColor(pronostico.state)}`}>
                      {(pronostico.state || 'pendiente').toUpperCase()}
                    </span>
                    <span className="text-white/60 text-sm">
                      {pronostico.date || 'N/A'} - {pronostico.hours || 'N/A'}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-4 mb-2">
                    <div className="flex items-center space-x-2">
                      {pronostico.homeTeam?.logo && (
                        <img 
                          src={pronostico.homeTeam.logo} 
                          alt={pronostico.homeTeam.name || 'Equipo local'}
                          className="w-6 h-6 rounded-full"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      )}
                      <span className="text-white font-medium">
                        {pronostico.homeTeam?.name || 'Equipo Local'}
                      </span>
                    </div>
                    <span className="text-white/60">vs</span>
                    <div className="flex items-center space-x-2">
                      {pronostico.awayTeam?.logo && (
                        <img 
                          src={pronostico.awayTeam.logo} 
                          alt={pronostico.awayTeam.name || 'Equipo visitante'}
                          className="w-6 h-6 rounded-full"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      )}
                      <span className="text-white font-medium">
                        {pronostico.awayTeam?.name || 'Equipo Visitante'}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4 text-sm">
                    <span className="text-white/60">
                      <Target className="inline w-4 h-4 mr-1" />
                      {pronostico.prediction || 'N/A'}
                    </span>
                    <span className="text-white/60">
                      <TrendingUp className="inline w-4 h-4 mr-1" />
                      {pronostico.confidence || 0}%
                    </span>
                    <span className="text-white/60">
                      <span className="mr-1">@</span>
                      {pronostico.odds || 'N/A'}
                    </span>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleEdit(pronostico)}
                    className="p-2 text-blue-400 hover:bg-blue-400/20 rounded-lg transition-colors"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(pronostico.id)}
                    className="p-2 text-red-400 hover:bg-red-400/20 rounded-lg transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                                     {(pronostico.state === 'pendiente' || !pronostico.state) && (
                     <div className="flex space-x-1">
                       <button
                         onClick={() => handleStatusChange(pronostico.id, 'ganado')}
                         className="p-2 text-green-400 hover:bg-green-400/20 rounded-lg transition-colors"
                         title="Marcar como Ganado"
                       >
                         <CheckCircle size={16} />
                       </button>
                       <button
                         onClick={() => handleStatusChange(pronostico.id, 'perdido')}
                         className="p-2 text-red-400 hover:bg-red-400/20 rounded-lg transition-colors"
                         title="Marcar como Perdido"
                       >
                         <XCircle size={16} />
                       </button>
                       <button
                         onClick={() => handleStatusChange(pronostico.id, 'nulo')}
                         className="p-2 text-gray-400 hover:bg-gray-400/20 rounded-lg transition-colors"
                         title="Marcar como Nulo"
                       >
                         <XCircle size={16} />
                       </button>
                     </div>
                   )}
                 </div>
               </div>
             </div>
           ))}
           
           {/* Paginación */}
           {totalPages > 1 && (
             <div className="flex items-center justify-center space-x-2 mt-6">
               <button
                 onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                 disabled={currentPage === 1}
                 className="px-3 py-1 rounded-lg bg-white/10 text-white hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
               >
                 Anterior
               </button>
               
               <span className="text-white/60 text-sm">
                 Página {currentPage} de {totalPages}
               </span>
               
               <button
                 onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                 disabled={currentPage === totalPages}
                 className="px-3 py-1 rounded-lg bg-white/10 text-white hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
               >
                 Siguiente
               </button>
             </div>
           )}
         </>
       )}
     </div>
   )}

      {/* Modal para crear/editar pronóstico */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-slate-700 shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white">
                  {editingPronostico ? 'Editar Pronóstico' : 'Nuevo Pronóstico'}
                </h3>
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    resetForm();
                  }}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  <XCircle size={24} />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Equipo Local con autocompletado */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Equipo Local
                    </label>
                    <div className="relative">
                                                             <div className="relative">
                  <input
                    type="text"
                    value={formData.homeTeamName}
                    onChange={(e) => handleTeamInputChange(e.target.value, 'home')}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSearchSubmit(e, 'home');
                      }
                    }}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-slate-400 pr-10"
                    placeholder="Buscar equipo local o escribir nuevo..."
                    required
                  />
                  <button
                    type="button"
                    onClick={(e) => handleSearchSubmit(e, 'home')}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-slate-400 hover:text-white transition-colors"
                  >
                    <Search size={16} />
                  </button>
                </div>
                    </div>
                    
                                         {/* Sugerencias para equipo local */}
                     {showHomeSuggestions && filteredHomeTeams.length > 0 && (
                       <div className="absolute z-10 w-full mt-1 bg-slate-700 border border-slate-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                         {filteredHomeTeams.map((team, index) => (
                           <button
                             key={index}
                             type="button"
                             onClick={() => selectTeam(team, 'home')}
                             className="w-full px-3 py-2 text-left hover:bg-slate-600 flex items-center space-x-3 transition-colors"
                           >
                             <img 
                               src={team.logo} 
                               alt={team.name}
                               className="w-6 h-6 rounded-full"
                               onError={(e) => e.target.style.display = 'none'}
                             />
                             <span className="text-white">{team.name}</span>
                           </button>
                         ))}
                         <div className="px-3 py-2 text-xs text-slate-400 border-t border-slate-600">
                           O escribe un nuevo equipo
                         </div>
                       </div>
                     )}
                     
                     {showHomeSuggestions && filteredHomeTeams.length === 0 && formData.homeTeamName && (
                       <div className="absolute z-10 w-full mt-1 bg-slate-700 border border-slate-600 rounded-lg shadow-lg p-3">
                         <div className="text-xs text-slate-400 text-center">
                           No se encontraron equipos. Puedes escribir un nuevo equipo.
                         </div>
                       </div>
                     )}
                  </div>

                                     {/* Logo Equipo Local (auto-completado) */}
                   <div>
                     <label className="block text-sm font-medium text-slate-300 mb-2">
                       Logo Equipo Local (URL)
                     </label>
                     <input
                       type="url"
                       value={formData.homeTeamLogo}
                       onChange={(e) => setFormData({...formData, homeTeamLogo: e.target.value})}
                       className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-slate-400"
                       placeholder="Se auto-completa al seleccionar equipo o escribe manualmente"
                       required
                     />
                   </div>

                  {/* Equipo Visitante con autocompletado */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Equipo Visitante
                    </label>
                    <div className="relative">
                                                             <div className="relative">
                  <input
                    type="text"
                    value={formData.awayTeamName}
                    onChange={(e) => handleTeamInputChange(e.target.value, 'away')}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSearchSubmit(e, 'away');
                      }
                    }}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-slate-400 pr-10"
                    placeholder="Buscar equipo visitante o escribir nuevo..."
                    required
                  />
                  <button
                    type="button"
                    onClick={(e) => handleSearchSubmit(e, 'away')}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-slate-400 hover:text-white transition-colors"
                  >
                    <Search size={16} />
                  </button>
                </div>
                    </div>
                    
                                         {/* Sugerencias para equipo visitante */}
                     {showAwaySuggestions && filteredAwayTeams.length > 0 && (
                       <div className="absolute z-10 w-full mt-1 bg-slate-700 border border-slate-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                         {filteredAwayTeams.map((team, index) => (
                           <button
                             key={index}
                             type="button"
                             onClick={() => selectTeam(team, 'away')}
                             className="w-full px-3 py-2 text-left hover:bg-slate-600 flex items-center space-x-3 transition-colors"
                           >
                             <img 
                               src={team.logo} 
                               alt={team.name}
                               className="w-6 h-6 rounded-full"
                               onError={(e) => e.target.style.display = 'none'}
                             />
                             <span className="text-white">{team.name}</span>
                           </button>
                         ))}
                         <div className="px-3 py-2 text-xs text-slate-400 border-t border-slate-600">
                           O escribe un nuevo equipo
                         </div>
                       </div>
                     )}
                     
                     {showAwaySuggestions && filteredAwayTeams.length === 0 && formData.awayTeamName && (
                       <div className="absolute z-10 w-full mt-1 bg-slate-700 border border-slate-600 rounded-lg shadow-lg p-3">
                         <div className="text-xs text-slate-400 text-center">
                           No se encontraron equipos. Puedes escribir un nuevo equipo.
                         </div>
                       </div>
                     )}
                  </div>

                                     {/* Logo Equipo Visitante (auto-completado) */}
                   <div>
                     <label className="block text-sm font-medium text-slate-300 mb-2">
                       Logo Equipo Visitante (URL)
                     </label>
                     <input
                       type="url"
                       value={formData.awayTeamLogo}
                       onChange={(e) => setFormData({...formData, awayTeamLogo: e.target.value})}
                       className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-slate-400"
                       placeholder="Se auto-completa al seleccionar equipo o escribe manualmente"
                       required
                     />
                   </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Confianza (%)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={formData.confidence}
                      onChange={(e) => setFormData({...formData, confidence: e.target.value})}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-slate-400"
                      placeholder="70"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Cuota
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="1"
                      value={formData.odds}
                      onChange={(e) => setFormData({...formData, odds: e.target.value})}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-slate-400"
                      placeholder="1.85"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Pronóstico
                    </label>
                    <input
                      type="text"
                      value={formData.prediction}
                      onChange={(e) => setFormData({...formData, prediction: e.target.value})}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-slate-400"
                      placeholder="1, X, 2, Over 2.5, etc."
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Fecha
                    </label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({...formData, date: e.target.value})}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Hora
                    </label>
                    <input
                      type="time"
                      value={formData.time}
                      onChange={(e) => setFormData({...formData, time: e.target.value})}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                      required
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t border-slate-700">
                  <button
                    type="button"
                    onClick={() => {
                      setIsModalOpen(false);
                      resetForm();
                    }}
                    className="px-6 py-2 text-slate-300 border border-slate-600 rounded-lg hover:bg-slate-700 hover:text-white transition-all duration-200"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 shadow-lg"
                  >
                    {editingPronostico ? 'Actualizar' : 'Crear'} Pronóstico
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PronosticosManager; 